import { useEffect, useRef, useState } from 'react';
import { renderAsync } from 'docx-preview';
import { FiCheck, FiDownload, FiFileText } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';
import Modal from './Modal';
import { QuotationTemplate } from '../types';
import { taskService } from '../services/taskService';
import { apiErrorMessage } from '../utils/apiError';
import { QuotationDraft } from '../utils/quotationDraft';

interface QuotationDraftModalProps {
  isOpen: boolean;
  taskId?: string;
  templates: QuotationTemplate[];
  draft: QuotationDraft | null;
  onClose: () => void;
  onUploaded?: () => void | Promise<void>;
}

const readError = async (error: unknown, fallback = 'Could not open this template.') => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as { error?: string };
      if (parsed.error) return parsed.error;
    } catch {
      /* the body was not the API error shape */
    }
  }
  if (data && typeof data === 'object' && 'error' in data) {
    const message = (data as { error?: unknown }).error;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return apiErrorMessage(error, fallback);
};

const QuotationDraftModal = ({ isOpen, taskId, templates, draft, onClose, onUploaded }: QuotationDraftModalProps) => {
  const pageRef = useRef<HTMLDivElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [file, setFile] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState('quotation.docx');
  const [opening, setOpening] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedId(null);
    setFile(null);
    setError(null);
    setOpening(false);
    setConfirming(false);
  }, [isOpen]);

  const isPdf = Boolean(file && (file.type.includes('pdf') || fileName.toLowerCase().endsWith('.pdf')));

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    const host = pageRef.current;
    if (!file || !host) return;
    let cancelled = false;
    host.replaceChildren();
    renderAsync(file, host, undefined, { inWrapper: true, breakPages: true }).then(() => {
      if (cancelled) return;
      host.querySelectorAll('section').forEach((section) => section.setAttribute('contenteditable', 'true'));
    }).catch(() => {
      if (!cancelled) setError('Could not show this Word template.');
    });
    return () => {
      cancelled = true;
    };
  }, [file]);

  const openTemplate = async (template: QuotationTemplate) => {
    if (!draft) return;
    setSelectedId(template.documentId);
    setFileName(template.fileName);
    setFile(null);
    setError(null);
    setOpening(true);
    try {
      const filled = await taskService.fillQuotationTemplate(template.documentId, draft);
      setFile(filled);
    } catch (caught) {
      setError(await readError(caught));
    } finally {
      setOpening(false);
    }
  };

  const confirm = async () => {
    if (!file || !taskId) return;
    setConfirming(true);
    try {
      const pdf = isPdf ? file : await taskService.convertQuotationToPdf(file);
      await taskService.uploadAttachment(
        taskId,
        new File([pdf], 'Quotation.pdf', { type: 'application/pdf' })
      );
      await onUploaded?.();
      toast.success('Quotation attached.');
      onClose();
    } catch (caught) {
      toast.error(await readError(caught, 'Could not upload the quotation.'));
    } finally {
      setConfirming(false);
    }
  };

  const download = () => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const selected = templates.find((template) => template.documentId === selectedId);

  return (
    <Modal
      isOpen={isOpen}
      title={selected ? selected.fileName : 'Choose a template'}
      description={
        selected
          ? isPdf
            ? 'The company template.'
            : 'The company template, filled from this enquiry. Edit the text in the document.'
          : 'Templates from the quotation folder.'
      }
      icon={<FiFileText />}
      size="xl"
      onClose={onClose}
      footer={
        selected ? (
          <div className="flex items-center gap-2">
            <Button variant="secondary" disabled={confirming} onClick={() => { setSelectedId(null); setFile(null); setError(null); }}>
              Back
            </Button>
            {file && (
              <Button variant="secondary" icon={<FiDownload />} disabled={confirming} onClick={download}>
                {isPdf ? 'Download PDF' : 'Download Word'}
              </Button>
            )}
            {file && taskId && (
              <Button variant="primary" icon={<FiCheck />} loading={confirming} onClick={confirm}>
                {confirming ? 'Confirming…' : 'Confirm'}
              </Button>
            )}
          </div>
        ) : (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      {!selected && (
        <ul className="space-y-2">
          {templates.length === 0 && (
            <li className="text-body text-ink-subtle">The quotation folder has no templates.</li>
          )}
          {templates.map((template) => (
            <li key={template.documentId}>
              <button
                type="button"
                onClick={() => openTemplate(template)}
                className="w-full rounded-card border border-line px-4 py-3 text-left text-body font-medium text-ink hover:bg-surface-muted"
              >
                {template.fileName}
              </button>
            </li>
          ))}
        </ul>
      )}

      {selected && opening && <LoadingSpinner label="Opening template" />}
      {selected && error && <p className="text-body text-danger">{error}</p>}
      {selected && file && isPdf && previewUrl && (
        <iframe title={fileName} src={previewUrl} className="h-[75vh] w-full rounded-card bg-white" />
      )}
      {selected && file && !isPdf && (
        <div ref={pageRef} className="overflow-auto rounded-card bg-surface-muted" />
      )}
    </Modal>
  );
};

export default QuotationDraftModal;
